import React, { useState, useEffect } from "react";
import axiosInstance from "../../services/auth";
import "./Loan.css";
import { toast } from "react-toastify";

const LoanForm = ({ selectedCompany, companyId, loan, onClose }) => {
  // Get the current user data (for createdBy and default repayment enteredBy)
  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));
  const currentUserId = authData?._id;

  const [employee, setEmployee] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch employees for the dropdown (by companyId)
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axiosInstance.get(`/loan/employee/${companyId}`);
        setEmployee(response.data.data);
      } catch (error) {
        toast.error("Error fetching employees");
      }
    };
    fetchEmployees();
  }, [companyId]);

  // Initialize form state based on whether this is a new or existing loan
  const initialFormData = loan
    ? {
        employeeId: loan.employeeId || "",
        applicationNo: loan.applicationNo || "",
        loanDate: loan.loanDate
          ? new Date(loan.loanDate).toISOString().split("T")[0]
          : "",
        loanType: loan.loanType || "",
        loanAmount: loan.loanAmount || "",
        status: loan.status || "Pending",

        // employeeSearch is used only for the dropdown display
        employeeSearch: loan.employee ? loan.employee.name : "",
        showEmployeeDropdown: false,
      }
    : {
        employeeId: "",
        applicationNo: "",
        loanDate: "",
        loanType: "",
        loanAmount: "",
        employeeSearch: "",
        showEmployeeDropdown: false,
      };

  const [formData, setFormData] = useState(initialFormData);

  // Remove any temporary properties before submitting
  const sanitizeFormData = (data) => {
    const sanitizedData = { ...data };
    delete sanitizedData.showEmployeeDropdown;
    delete sanitizedData.employeeSearch;
    return sanitizedData;
  };

  // Close modal if clicking outside of the dialog
  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  // Prevent background scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Handle change for top-level fields and repayments (using dotted names)
  const handleChange = (e, index) => {
    const { name, value } = e.target;
    if (name.startsWith("repayments")) {
      // Expected name format: "repayments.<index>.<field>"
      const parts = name.split(".");
      const idx = parseInt(parts[1]);
      const field = parts[2];
      const updatedRepayments = [...formData.repayments];
      updatedRepayments[idx] = {
        ...updatedRepayments[idx],
        [field]: value,
      };
      setFormData((prev) => ({ ...prev, repayments: updatedRepayments }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Submit the form data (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.employeeId && !loan) {
      toast.error("Please select an employee.");
      return;
    }

    setLoading(true);
    const sanitizedData = sanitizeFormData(formData);

    try {
      let response;

      if (loan) {
        // Update an existing loan
        // console.log("update loan", loan);
        response = await axiosInstance.patch(
          `/loan/${loan.loanDocId}/${loan.loanId}`,
          sanitizedData,
        );
      } else {
        // Create a new loan – add createdBy field
        const payload = { ...sanitizedData, createdBy: currentUserId };
        response = await axiosInstance.post("/loan", payload);
      }

      toast.success(
        loan ? "Loan updated successfully" : "Loan created successfully",
      );
      onClose();
    } catch (error) {
      // console.error("Full error object:", error);
      // console.error("Error response:", error.response);

      let errorMessage = "Error saving loan";

      if (error.response?.data?.message) {
        errorMessage = Array.isArray(error.response.data.message)
          ? error.response.data.message.join(", ") // Handle array of messages
          : error.response.data.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      onClick={handleOutsideClick}
      style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered"
        role="document"
      >
        <div className="modal-content loan_model">
          <div className="modal-header text-white text-center vesel_form">
            <h5 className="modal-title">
              {loan ? "Edit Loan" : "Create Loan"}
            </h5>
            <button
              type="button"
              className="btn-close text-white"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body vesel_form">
            <form onSubmit={handleSubmit}>
              {/* Loan Details Section */}
              <div className="card shadow-sm mb-4 rounded-3">
                <div className="card-body">
                  <h5 className="card-title text-center mb-4">Loan Details</h5>
                  <div className="row">
                    <div className="col-md-6 mb-3 position-relative">
                      <label className="form-label">Employee</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type employee name or code"
                        name="employeeSearch"
                        value={formData.employeeSearch}
                        disabled={!!loan} // 🔒 Disable if editing
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            employeeSearch: e.target.value,
                            employeeId: "",
                          }))
                        }
                        onFocus={() =>
                          setFormData((prev) => ({
                            ...prev,
                            showEmployeeDropdown: true,
                          }))
                        }
                        onBlur={() => {
                          setTimeout(() => {
                            setFormData((prev) => ({
                              ...prev,
                              showEmployeeDropdown: false,
                            }));
                          }, 150);
                        }}
                        required
                      />

                      {formData.showEmployeeDropdown && (
                        <ul
                          className="list-group position-absolute"
                          style={{
                            zIndex: 1200,
                            width: "100%",
                            maxHeight: 220,
                            overflowY: "auto",
                          }}
                        >
                          {employee
                            .filter(
                              (item) =>
                                (item.name || "")
                                  .toLowerCase()
                                  .includes(
                                    formData.employeeSearch.toLowerCase(),
                                  ) ||
                                (item.employeeNo
                                  ? item.employeeNo.toString()
                                  : ""
                                ).includes(
                                  formData.employeeSearch.toLowerCase(),
                                ),
                            )
                            .map((item) => (
                              <li
                                key={item._id}
                                className="list-group-item list-group-item-action"
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    employeeId: item._id,
                                    employeeSearch: item.name,
                                    showEmployeeDropdown: false,
                                  }))
                                }
                              >
                                {`${item.name} - ${item.employeeNo}`}
                              </li>
                            ))}
                          {employee.filter(
                            (item) =>
                              (item.name || "")
                                .toLowerCase()
                                .includes(
                                  formData.employeeSearch.toLowerCase(),
                                ) ||
                              (item.employeeNo
                                ? item.employeeNo.toString()
                                : ""
                              ).includes(formData.employeeSearch.toLowerCase()),
                          ).length === 0 && (
                            <li className="list-group-item list-group-item-action">
                              No results found
                            </li>
                          )}
                        </ul>
                      )}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Loan Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="loanDate"
                        value={formData.loanDate}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Loan Type</label>
                      <input
                        type="text"
                        className="form-control"
                        name="loanType"
                        value={formData.loanType}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Loan Amount</label>
                      <input
                        type="number"
                        className="form-control"
                        name="loanAmount"
                        value={formData.loanAmount}
                        onChange={handleChange}
                        required
                        min={0}
                        step="any"
                      />
                    </div>
                  </div>
                  <div className="row"></div>
                </div>
              </div>

              <div className="mt-4 text-center">
                <button type="submit" className="button-2" disabled={loading}>
                  {loading
                    ? loan
                      ? "Updating"
                      : "Creating"
                    : loan
                      ? "Update Loan"
                      : "Create Loan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanForm;
